import React, { useEffect, useState, useContext } from 'react'
import ModalTest from '../../BaseModal'
import * as S from '../../styles/modal-style'
import { X, Plus, Check } from 'lucide-react'
import axios from '../../../config/axios'
import { FeedbackContext } from '../../../contexts/FeedbackContext'

interface Sector {
  id: number
  name: string
}

interface Area {
  id: number
  name: string
}

interface CreateProductionSectorModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (sectorId: number, sectorName: string) => void
  serviceName: string
}

const CreateProductionSectorModal: React.FC<CreateProductionSectorModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  serviceName,
}) => {
  const { setFeedback } = useContext(FeedbackContext)
  const [productionArea, setProductionArea] = useState<Area | null>(null)
  const [sectors, setSectors] = useState<Sector[]>([])
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isCreatingSector, setIsCreatingSector] = useState(false)
  const [newSectorName, setNewSectorName] = useState('')

  useEffect(() => {
    if (!isOpen) return

    const fetchAreaAndSectors = async () => {
      try {
        setIsLoading(true)
        const areasRes = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage/util/areas`,
        )
        const areas: Area[] = areasRes.data || []
        const prod = areas.find(
          (a) =>
            a.name.toLowerCase().includes('produç') ||
            a.name.toLowerCase().includes('produc') ||
            a.name.toLowerCase() === 'produção',
        ) || areas[0]

        if (prod) {
          setProductionArea(prod)
          const sectorsRes = await axios.get(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage/util/sectors`,
            { params: { areaId: prod.id } },
          )
          const fetchedSectors: Sector[] = sectorsRes.data || []
          setSectors(fetchedSectors)
          if (fetchedSectors.length > 0) {
            setSelectedSectorId(fetchedSectors[0].id)
          }
        }
      } catch (err) {
        console.error('Erro ao buscar áreas/setores de produção:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAreaAndSectors()
  }, [isOpen])

  if (!isOpen) return null

  const handleCreateSector = async () => {
    if (!newSectorName.trim() || !productionArea) return
    try {
      setIsLoading(true)
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage/util/sectors`,
        {
          name: newSectorName.trim(),
          areaId: productionArea.id,
        },
      )
      const created: Sector = response.data
      setSectors((prev) => [...prev, created])
      setSelectedSectorId(created.id)
      setNewSectorName('')
      setIsCreatingSector(false)
      setFeedback({
        isOpen: true,
        message: `Setor "${created.name}" criado com sucesso!`,
        statusOk: true,
      })
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Erro ao criar setor'
      setFeedback({ isOpen: true, message: msg, statusOk: false })
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirm = () => {
    if (!selectedSectorId) {
      setFeedback({ isOpen: true, message: 'Selecione um setor da Área de Produção.', statusOk: false })
      return
    }
    const sector = sectors.find((s) => s.id === selectedSectorId)
    if (sector) {
      onConfirm(sector.id, sector.name)
    }
  }

  return (
    <ModalTest>
      <S.ContainerOfModal width="36rem">
        <S.Header>
          <S.HeaderTitle>Setor da Área de Produção</S.HeaderTitle>
          <S.CloseDiv onClick={onClose}>
            <X size={20} color="#000" />
          </S.CloseDiv>
        </S.Header>

        <S.FormContainer style={{ gap: '16px', padding: '16px 0' }}>
          <p style={{ fontSize: '14px', color: '#4b5563', margin: 0 }}>
            Vincular o serviço <strong style={{ color: '#111827' }}>&quot;{serviceName}&quot;</strong> a um setor da Área de Produção:
          </p>

          {!isCreatingSector ? (
            <S.InputDiv style={{ width: '100%' }}>
              <S.Label>Setor de Produção</S.Label>
              <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                <select
                  value={selectedSectorId || ''}
                  onChange={(e) => setSelectedSectorId(Number(e.target.value))}
                  style={{
                    flex: 1,
                    height: '42px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    padding: '0 12px',
                    fontSize: '14px',
                    backgroundColor: '#fff',
                  }}
                  disabled={isLoading}
                >
                  {sectors.map((sector) => (
                    <option key={sector.id} value={sector.id}>
                      {sector.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setIsCreatingSector(true)}
                  style={{
                    height: '42px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: '1px solid #0090d3',
                    backgroundColor: '#f0f9ff',
                    color: '#0090d3',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Plus size={16} /> Novo Setor
                </button>
              </div>
            </S.InputDiv>
          ) : (
            <S.InputDiv style={{ width: '100%' }}>
              <S.Label>Nome do Novo Setor de Produção</S.Label>
              <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                <S.Input
                  type="text"
                  placeholder="Ex: Corte / Costura"
                  value={newSectorName}
                  onChange={(e) => setNewSectorName(e.target.value)}
                  style={{ flex: 1, height: '42px' }}
                />
                <button
                  type="button"
                  onClick={handleCreateSector}
                  disabled={isLoading || !newSectorName.trim()}
                  style={{
                    height: '42px',
                    padding: '0 16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#0090d3',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Check size={16} /> Salvar
                </button>
                <button
                  type="button"
                  onClick={() => setIsCreatingSector(false)}
                  style={{
                    height: '42px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#fff',
                    color: '#64748b',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
              </div>
            </S.InputDiv>
          )}

          <S.ButtonContainer style={{ justifyContent: 'flex-end', marginTop: '12px' }}>
            <S.Button
              type="button"
              style={{ backgroundColor: '#e2e8f0', color: '#334155' }}
              onClick={onClose}
            >
              Cancelar
            </S.Button>
            <S.Button
              type="button"
              style={{ backgroundColor: '#0090d3', color: '#fff' }}
              onClick={handleConfirm}
              disabled={isLoading || !selectedSectorId}
            >
              Confirmar e Criar Serviço
            </S.Button>
          </S.ButtonContainer>
        </S.FormContainer>
      </S.ContainerOfModal>
    </ModalTest>
  )
}

export default CreateProductionSectorModal
